// app/App.tsx — Root application component with providers

import { Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { ToastProvider, TooltipProvider } from '@/components/ui';
import { UpdatePrompt } from '@/components/layout/UpdatePrompt';
import { router } from './Router';
import { ThemeProvider } from './ThemeProvider';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,        // 5 minutes
      retry: 2,
      refetchOnWindowFocus: false,       // Family data doesn't need aggressive refetch
    },
    mutations: {
      retry: 1,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <ToastProvider>
            <Suspense fallback={<AppLoader />}>
              <RouterProvider router={router} />
            </Suspense>
            <UpdatePrompt />
          </ToastProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

// Full-screen loader shown while lazy chunks download
function AppLoader() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100dvh',
        backgroundColor: 'var(--color-surface)',
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          border: '3px solid var(--color-neutral-200)',
          borderTopColor: 'var(--color-primary)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }}
        aria-label="Loading"
        role="status"
      />
    </div>
  );
}
